import React, { useState, useEffect } from 'react';
import { FaSave, FaEye, FaEyeSlash } from 'react-icons/fa';
import { Alert, Button, Modal, Form, Spinner, InputGroup } from 'react-bootstrap';

// One auth mode per cloud. The platform's workload identity (attached to the pods)
// is the implicit default for everything; this modal is for *alternate* stored
// credentials used by specific data sources that the workload identity can't reach.
const AUTH_MODE_FOR_CLOUD = {
  AWS: 'ACCESS_KEY',
  AZURE: 'SERVICE_PRINCIPAL'
};

const emptyForm = (initialCloud) => ({
  cloud: initialCloud || 'AWS',
  displayName: '',
  accountIdentifier: '',
  tenantId: '',
  authMode: AUTH_MODE_FOR_CLOUD[initialCloud || 'AWS'],
  accessKeyId: '',
  secretAccessKey: '',
  clientId: '',
  clientSecret: '',
  secretSource: 'INLINE',
  secretReference: '',
  enabled: true
});

// Deployment cloud (aws|azure|standalone) constrains which Cloud options are offered.
const cloudsForDeployment = (deploymentCloud) => {
  if (deploymentCloud === 'aws') return ['AWS'];
  if (deploymentCloud === 'azure') return ['AZURE'];
  return ['AWS', 'AZURE'];
};

// Stored-secret source options. INLINE always available. KV / SM offered based on the
// account's cloud (cross-cloud refs are technically possible but a footgun, so disallow).
const secretSourceOptions = (formCloud) => {
  const opts = [
    { value: 'INLINE', label: 'Inline (encrypted in DB)' }
  ];
  if (formCloud === 'AWS') {
    opts.push({ value: 'SECRETSMANAGER', label: 'AWS Secrets Manager reference' });
  }
  if (formCloud === 'AZURE') {
    opts.push({ value: 'KEYVAULT', label: 'Azure Key Vault reference' });
  }
  return opts;
};

const secretReferencePlaceholder = (secretSource) => {
  if (secretSource === 'KEYVAULT') return 'keyvault://my-vault/c2pp-azure-sp-creds';
  if (secretSource === 'SECRETSMANAGER') return 'secretsmanager://arn:aws:secretsmanager:us-east-1:123456789012:secret:c2pp-aws-keys-AbCdEf';
  return '';
};

const secretReferenceHelper = (secretSource) => {
  if (secretSource === 'KEYVAULT') {
    return 'The secret in Key Vault must hold a JSON value: {"clientId":"...","clientSecret":"..."}. The platform fetches it using the workload identity.';
  }
  if (secretSource === 'SECRETSMANAGER') {
    return 'The secret in Secrets Manager must hold a JSON value: {"accessKeyId":"...","secretAccessKey":"..."}. The platform fetches it using the workload identity.';
  }
  return '';
};

const CloudAccountFormModal = (props) => {
  const allowedClouds = cloudsForDeployment(props.deploymentCloud);
  const defaultCloud = allowedClouds[0];
  const [form, setForm] = useState(emptyForm(defaultCloud));
  const [showSecret, setShowSecret] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  const editing = !!props.account;

  useEffect(() => {
    if (props.show) {
      if (props.account) {
        setForm({
          ...emptyForm(defaultCloud),
          ...props.account,
          enabled: props.account.enabled !== false,
          secretAccessKey: '',
          clientSecret: ''
        });
      } else {
        setForm(emptyForm(defaultCloud));
      }
      setShowSecret(false);
      setShowClientSecret(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, props.account]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const onCloudChange = (cloud) => {
    setForm((f) => ({
      ...f,
      cloud,
      authMode: AUTH_MODE_FOR_CLOUD[cloud],
      accessKeyId: '',
      secretAccessKey: '',
      clientId: '',
      clientSecret: '',
      secretSource: 'INLINE',
      secretReference: '',
      tenantId: cloud === 'AZURE' ? f.tenantId : ''
    }));
  };

  const isAws = form.cloud === 'AWS';
  const isAzure = form.cloud === 'AZURE';
  const sourceOptions = secretSourceOptions(form.cloud);
  const usingPointer = form.secretSource === 'KEYVAULT' || form.secretSource === 'SECRETSMANAGER';

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only include secrets that were actually typed
      if (!payload.secretAccessKey) delete payload.secretAccessKey;
      if (!payload.clientSecret) delete payload.clientSecret;
      if (!payload.accessKeyId) delete payload.accessKeyId;
      if (!payload.clientId && isAws) delete payload.clientId;
      // Drop pointer / inline payload fields that don't apply to the chosen secretSource.
      if (payload.secretSource !== 'KEYVAULT' && payload.secretSource !== 'SECRETSMANAGER') {
        delete payload.secretReference;
      } else {
        // Pointer mode: don't ship inline secret material — server will reject mixed payloads.
        delete payload.accessKeyId;
        delete payload.secretAccessKey;
        delete payload.clientId;
        delete payload.clientSecret;
      }
      await props.onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onHide={props.onClose} show={props.show} size='lg'>
      <Modal.Header closeButton>
        {editing ? 'Edit alternate credential' : 'Add alternate credential'}
      </Modal.Header>
      <Modal.Body>
        {props.error && (
          <Alert variant='danger'>{props.error}</Alert>
        )}

        <Alert variant='info'>
          Alternate credentials are a fallback for specific data sources the platform's
          workload identity can't reach. Most deployments never need one — the attached
          identity (IAM role or Managed Identity) covers everything.
        </Alert>

        <Form.Group>
          <Form.Label>Display name</Form.Label>
          <Form.Control
            type='text'
            value={form.displayName}
            onChange={(e) => update('displayName', e.target.value)}
            placeholder='e.g. Legacy RDS credentials'
          />
        </Form.Group>

        <Form.Group className='mt-3'>
          <Form.Label>Cloud</Form.Label>
          <Form.Control
            as='select'
            data-testid='cloud-account-cloud-select'
            value={form.cloud}
            onChange={(e) => onCloudChange(e.target.value)}
            disabled={editing || allowedClouds.length === 1}
          >
            {allowedClouds.includes('AWS') && <option value='AWS'>AWS (access key)</option>}
            {allowedClouds.includes('AZURE') && <option value='AZURE'>Azure (service principal)</option>}
          </Form.Control>
          {editing && (
            <Form.Text className='text-muted'>
              Cloud cannot be changed after creation.
            </Form.Text>
          )}
          {!editing && allowedClouds.length === 1 && (
            <Form.Text className='text-muted'>
              This deployment is configured as {props.deploymentCloud === 'aws' ? 'AWS-only' : 'Azure-only'} (C2_CLOUD={props.deploymentCloud}).
            </Form.Text>
          )}
        </Form.Group>

        <Form.Group className='mt-3'>
          <Form.Label>
            {isAws ? 'AWS account ID' : 'Azure subscription ID'}
          </Form.Label>
          <Form.Control
            type='text'
            value={form.accountIdentifier}
            onChange={(e) => update('accountIdentifier', e.target.value)}
            placeholder={isAws ? '123456789012' : '00000000-0000-0000-0000-000000000000'}
          />
        </Form.Group>

        {sourceOptions.length > 1 && (
          <Form.Group className='mt-3'>
            <Form.Label>Secret storage</Form.Label>
            <Form.Control
              as='select'
              data-testid='cloud-account-secret-source-select'
              value={form.secretSource}
              onChange={(e) => update('secretSource', e.target.value)}
            >
              {sourceOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Control>
            <Form.Text className='text-muted'>
              Inline encrypts the secret in the platform DB under the deployment's encryption key.
              Pointer modes keep the secret in the cloud's secret store and resolve it at probe time
              using the workload identity.
            </Form.Text>
          </Form.Group>
        )}

        {usingPointer && (
          <Form.Group className='mt-3'>
            <Form.Label>
              Secret reference
              {editing && form.hasStoredCredential && (
                <span className='text-muted ml-2'>
                  (leave blank to keep the stored reference)
                </span>
              )}
            </Form.Label>
            <Form.Control
              type='text'
              value={form.secretReference || ''}
              onChange={(e) => update('secretReference', e.target.value)}
              placeholder={secretReferencePlaceholder(form.secretSource)}
            />
            <Form.Text className='text-muted'>
              {secretReferenceHelper(form.secretSource)}
            </Form.Text>
          </Form.Group>
        )}

        {isAws && form.secretSource === 'INLINE' && (
          <>
            <Form.Group className='mt-3'>
              <Form.Label>Access key ID</Form.Label>
              <Form.Control
                type='text'
                value={form.accessKeyId}
                onChange={(e) => update('accessKeyId', e.target.value)}
                placeholder='AKIA...'
              />
            </Form.Group>
            <Form.Group className='mt-3'>
              <Form.Label>
                Secret access key
                {editing && form.hasStoredCredential && (
                  <span className='text-muted ml-2'>
                    (leave blank to keep the stored secret)
                  </span>
                )}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showSecret ? 'text' : 'password'}
                  value={form.secretAccessKey}
                  onChange={(e) => update('secretAccessKey', e.target.value)}
                  placeholder={editing ? '••••••••' : 'Enter secret access key'}
                />
                <Button
                  variant='outline-secondary'
                  onClick={() => setShowSecret(!showSecret)}
                  className='d-flex align-items-center justify-content-center'
                  style={{ maxWidth: '42px', borderStyle: 'none' }}
                >
                  {showSecret ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>
          </>
        )}

        {isAzure && (
          <Form.Group className='mt-3'>
            <Form.Label>Tenant ID</Form.Label>
            <Form.Control
              type='text'
              value={form.tenantId}
              onChange={(e) => update('tenantId', e.target.value)}
              placeholder='00000000-0000-0000-0000-000000000000'
            />
          </Form.Group>
        )}

        {isAzure && form.secretSource === 'INLINE' && (
          <>
            <Form.Group className='mt-3'>
              <Form.Label>Client ID</Form.Label>
              <Form.Control
                type='text'
                value={form.clientId}
                onChange={(e) => update('clientId', e.target.value)}
                placeholder='00000000-0000-0000-0000-000000000000'
              />
            </Form.Group>
            <Form.Group className='mt-3'>
              <Form.Label>
                Client secret
                {editing && form.hasStoredCredential && (
                  <span className='text-muted ml-2'>
                    (leave blank to keep the stored secret)
                  </span>
                )}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showClientSecret ? 'text' : 'password'}
                  value={form.clientSecret}
                  onChange={(e) => update('clientSecret', e.target.value)}
                  placeholder={editing ? '••••••••' : 'Enter client secret'}
                />
                <Button
                  variant='outline-secondary'
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  className='d-flex align-items-center justify-content-center'
                  style={{ maxWidth: '42px', borderStyle: 'none' }}
                >
                  {showClientSecret ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>
          </>
        )}

        <Form.Group className='mt-3'>
          <Form.Check
            type='checkbox'
            label='Enabled'
            checked={form.enabled}
            onChange={(e) => update('enabled', e.target.checked)}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className='d-flex justify-content-end'>
        <Button onClick={props.onClose} variant='outline-secondary'>
          CANCEL
        </Button>
        <Button disabled={saving} onClick={save} variant='primary'>
          {saving ? (
            <Spinner className='mr-2' as='span' animation='border' size='sm' role='status' aria-hidden='true' />
          ) : (
            <FaSave className='mr-2' />
          )}
          SAVE
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloudAccountFormModal;
